import {
	type FrontendPlacementMessage,
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useCallback, useState } from 'react';

import { FormattedMessage, defineMessages } from 'react-intl';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import { useMountToHtmlElement } from '@post-office/components--post-office-team';

import { useMessageEvent } from '@post-office/message-lifecycle';

import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import { usePlacementApi } from '@post-office/use-placement-api';

import type { DismissEvent, Placement } from '@atlaskit/spotlight/types';

import { RovoOpsSpotlightCard } from './RovoOpsSpotlightCard';

const spotlightMessages = defineMessages({
	heading: {
		id: 'aiops-grogu.rovo-ops-cta-spotlight.heading',
		defaultMessage: 'Triage alerts with Rovo Ops',
		description: 'Heading for the Rovo Ops CTA spotlight.',
	},
	body: {
		id: 'aiops-grogu.rovo-ops-cta-spotlight.body',
		defaultMessage: 'Investigate and compare alerts, and find related resources to resolve faster.',
		description: 'Body for the Rovo Ops CTA spotlight.',
	},
	done: {
		id: 'aiops-grogu.rovo-ops-cta-spotlight.done',
		defaultMessage: 'Ok',
		description: 'Primary action label for Rovo Ops CTA spotlight.',
	},
});

type PlacementRenderProps = {
	onDismiss: () => void;
	onClick?: () => void;
};

type SpotlightPlacementConfig = {
	placementId: string;
	selector: string;
	popoverPlacement?: Placement;
};

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

export const createRovoOpsSpotlightPlacement = ({
	placementId,
	selector,
	popoverPlacement,
}: SpotlightPlacementConfig): {
	createFrontend: <U extends AnyProps = NoProps>(
		placementRenderer: PlacementRenderer<
			{
				render: FrontendStage<PlacementRenderProps>;
			},
			U
		>,
	) => PlacementMessageFrontendFactory<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>;
	PlacementFrontend: PlacementMessageFrontendFactory<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		{
			onClick?: () => void;
		}
	>;
	render: {
		type: 'render';
		validate: (input: unknown) => PlacementRenderProps;
		Component: (input: PlacementRenderProps) => JSX.Element | null;
	};
	MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
		render: FrontendStage<PlacementRenderProps>;
	}>;
} => {
	const placementConfig: PlacementBrowserConfig = {
		placementId,
		options: {
			choreographer: {
				isDefaultEnabled: true,
			},
		},
	};

	const placement = {
		createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
	};

	const createFrontend: <U extends AnyProps = NoProps>(
		placementRenderer: PlacementRenderer<
			{
				render: FrontendStage<PlacementRenderProps>;
			},
			U
		>,
	) => PlacementMessageFrontendFactory<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	> = placement.createFrontend;

	const PlacementFrontend: PlacementMessageFrontendFactory<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		{ onClick?: () => void }
	> = createFrontend<{ onClick?: () => void }>((Messages) => ({ onClick }) => {
		const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

		const { data, error, isLoading } = usePlacementApi<{
			messages: Array<EligibleMessage>;
		}>('/', {
			autoRefetch: {
				isEnabled: true,
				updateKey: placementId,
			},
		});

		const visibleMessages = data?.messages
			?.filter((message) => !dismissedIds.has(message.messageInstanceId))
			?.slice(0, 1);

		const handleDismiss = useCallback((messageInstanceId: string) => {
			setDismissedIds((previousIds) => new Set([...previousIds, messageInstanceId]));
		}, []);

		if (error) {
			throw error;
		}

		if (isLoading || !visibleMessages || visibleMessages.length === 0) {
			return null;
		}

		return (
			<>
				{visibleMessages.map((message) => (
					<Messages.render
						key={message.messageInstanceId}
						onDismiss={() => handleDismiss(message.messageInstanceId)}
						onClick={onClick}
						{...message}
					/>
				))}
			</>
		);
	});

	const render: {
		type: 'render';
		validate: (input: unknown) => PlacementRenderProps;
		Component: (input: PlacementRenderProps) => JSX.Element | null;
	} = PlacementFrontend.createFrontendStage({
		type: 'render',
		validate: noValidation<PlacementRenderProps>(),
		Component: ({ onDismiss, onClick }: PlacementRenderProps) => {
			const [isVisible, setIsVisible] = useState(true);
			const { messageDismissed } = useMessageEvent();
			const { onClosed, onAcknowledged } = usePostOfficeUserInteractions();

			const renderInline = useMountToHtmlElement({
				selectors: selector,
				as: 'inline',
				position: 'inside',
			});

			const dismiss = useCallback(
				(dismissEvent: DismissEvent) => {
					dismissEvent.preventDefault();
					dismissEvent.stopPropagation();
					setIsVisible(false);
					void messageDismissed();
					void onClosed({ interactionIdentifier: 'spotlight-dismissed' });
					onDismiss?.();
				},
				[messageDismissed, onClosed, onDismiss],
			);

			const complete = useCallback(
				(dismissEvent: DismissEvent) => {
					dismissEvent.preventDefault();
					dismissEvent.stopPropagation();
					setIsVisible(false);
					void messageDismissed();
					void onAcknowledged({
						interactionIdentifier: 'done',
						additionalMetadata: { actionTaken: true },
					});
					onClick?.();
					onDismiss?.();
				},
				[messageDismissed, onAcknowledged, onClick, onDismiss],
			);

			if (!isVisible) {
				return null;
			}

			return (
				<div>
					{renderInline(
						<RovoOpsSpotlightCard
							title={<FormattedMessage {...spotlightMessages.heading} />}
							body={<FormattedMessage {...spotlightMessages.body} />}
							buttonText={<FormattedMessage {...spotlightMessages.done} />}
							onDismiss={dismiss}
							onDone={complete}
							popoverPlacement={popoverPlacement}
						/>,
					)}
				</div>
			);
		},
	});

	const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
		render: FrontendStage<PlacementRenderProps>;
	}> = PlacementFrontend.createMessageFrontend({
		render,
	});

	return {
		createFrontend,
		PlacementFrontend,
		render,
		MessageFrontend,
	};
};
